import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import FormField from '@/components/common/form/FormField';
import { NS } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/redux';
import { clearAuthStatus, resetPassword } from '@/redux/slices/auth.slice';
import { CallToActionKind } from '@/types';
import { getApiErrorMessage } from '@/utils/millesimaUtils';
import styles from './ForgotPassword.module.scss';

const AlertMessage = dynamic(() => import('@/components/common/AlertMessage'), { ssr: false });
AlertMessage.displayName = 'AlertMessage';

export const ForgotPassword = ({ handleCancel }) => {
	const resetStatus = useAppSelector(({ auth }) => auth.status.reset);
	const dispatch = useAppDispatch();
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [message, setMessage] = useState<{ text: string; error: boolean }>();
	const { t } = useTranslation(NS.COMMON);

	const methods = useForm();
	const { handleSubmit } = methods;

	useEffect(() => {
		dispatch(clearAuthStatus({ name: 'reset' }));
	}, [clearAuthStatus]);

	useEffect(() => {
		if (submitted && resetStatus) {
			if (resetStatus.success) {
				setMessage({ error: false, text: t('forms.mailSent') });
			} else if (resetStatus.error) {
				setMessage({ error: true, text: getApiErrorMessage(t, resetStatus.error) });
			}
		}
	}, [resetStatus, submitted, t]);

	const onSubmit = (data) => {
		dispatch(resetPassword({ email: data.email }));
		setSubmitted(true);
		setMessage(undefined);
	};

	return (
		<div data-test-id="forgot-password" data-rbf="login-form-forgot-password">
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormField
						id="email-forgot-password"
						name="email"
						type="email"
						label={t('forms.email')}
						data-test-id="email-forgot-password"
						required={t('forms.requiredField')}
						pattern={{ value: /\S+@\S+\.\S+/, message: t('forms.validEmail') }}
					/>
					{message && (
						<div className={styles.message} data-test-id="forgot-password-message">
							<AlertMessage error={message.error} message={message.text} />
						</div>
					)}
					<div className={styles['submit-container']}>
						{message ? (
							<CallToActionContainer
								id="forgot-password-cancel"
								kind={CallToActionKind.SECONDARY}
								type="button"
								onClick={handleCancel}
							>
								{t('btn.continue')}
							</CallToActionContainer>
						) : (
							<>
								<CallToActionContainer
									id="forgot-password-cancel"
									kind={CallToActionKind.SECONDARY}
									type="button"
									onClick={handleCancel}
								>
									{t('btn.cancel')}
								</CallToActionContainer>
								<CallToActionContainer id="forgot-password-submit" type="submit">
									{t('btn.validate')}
								</CallToActionContainer>
							</>
						)}
					</div>
				</form>
			</FormProvider>
		</div>
	);
};

export default ForgotPassword;
